// ContactView.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers';
import { Image } from 'semantic-ui-react';
import cx from 'classnames';
import { useIntl } from 'react-intl';
import messages from '../../../../messages';

const getImageURL = (img) => {
  if (!img) return undefined;
  const scales = img.scales || {};
  const scale =
    scales.preview ||
    scales.teaser ||
    scales.large ||
    scales.mini ||
    scales.thumb ||
    scales.icon;

  const url = (scale && scale.download) || img.download;
  return url ? flattenToAppURL(url) : undefined;
};

const ContactView = ({ data = {}, content }) => {
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [accordions, setAccordions] = useState({
    research_priorities: false,
    functions: false,
    faculty: false,
  });

  const contactId = data?.contact?.[0]?.['@id'] || content?.['@id'] || null;

  const intl = useIntl();

  const defaultVisible = [
    'salutation',
    'name',
    'surname',
    'phone',
    'email',
    'institution',
    'department',
    'role',
    'research_priorities',
    'functions',
    'faculty',
    'street',
    'room',
    'postalcode',
    'location',
    'image',
  ];

  const visible = Array.isArray(data?.visibleFields)
    ? data.visibleFields
    : defaultVisible;

  const visibleSet = useMemo(() => new Set(visible), [visible]);

  useEffect(() => {
    if (!contactId) return;

    setLoading(true);
    const url = flattenToAppURL(contactId);
    const apiUrl = url.includes('++api++') ? url : `/++api++${url}`;

    fetch(apiUrl)
      .then((r) => r.json())
      .then((json) => setContact(json))
      .finally(() => setLoading(false));
  }, [contactId]);

  useEffect(() => {
    // bei Kontaktwechsel alles schließen
    setIsOpen(false);
    setAccordions({
      research_priorities: false,
      functions: false,
      faculty: false,
    });
  }, [contactId]);

  const values = useMemo(() => {
    if (!contact) return {};

    const normalizeFaculty = (raw) => {
      if (!raw) return [];
      if (Array.isArray(raw)) {
        return raw
          .map((item) => {
            if (!item) return null;
            if (typeof item === 'string') return item;
            if (typeof item === 'object') {
              return item.title || item.token || '';
            }
            return null;
          })
          .filter(Boolean);
      }
      if (typeof raw === 'string') {
        return [raw];
      }
      if (typeof raw === 'object' && (raw.title || raw.token)) {
        return [raw.title || raw.token];
      }
      return [];
    };

    return {
      salutation: contact.salutation,
      name: contact.name,
      surname: contact.surname,
      phone: contact.phone,
      email: contact.email,
      institution: contact.university || contact.institution,
      department: contact.department,
      role: contact.role,
      research_priorities: contact.research_priorities,
      functions: contact.functions,
      faculty: normalizeFaculty(contact.faculty),
      street: contact.street,
      room: contact.room,
      postalcode: contact.postalcode,
      location: contact.location,
      image: getImageURL(contact.image),
    };
  }, [contact]);

  if (!contactId || loading) {
    return (
      <div className="contact-block contact-block--loading">
        {intl.formatMessage(messages.contactLoading)}
      </div>
    );
  }

  if (!contact) return null;

  const getRichHtml = (field) => {
    if (!field || typeof field.data !== 'string') return '';
    const trimmed = field.data.trim();
    return trimmed || '';
  };

  const line1Parts = [];
  if (visibleSet.has('salutation') && values.salutation) {
    line1Parts.push(values.salutation);
  }
  if (visibleSet.has('name') && values.name) {
    line1Parts.push(values.name);
  }
  const line1 = line1Parts.join(' ');

  const line2Parts = [];
  if (visibleSet.has('surname') && values.surname) {
    line2Parts.push(values.surname);
  }
  const line2 = line2Parts.join(' ');

  const titleForAlt = [line1, line2].filter(Boolean).join(' ');

  const theme = data.theme || 'white';

  const institutionLabel =
    typeof data.institutionLabel === 'string'
      ? data.institutionLabel.trim()
      : '';

  const hasInstitution =
    visibleSet.has('institution') &&
    typeof values.institution === 'string' &&
    values.institution.trim() !== '';

  const hasInstitutionLabel = !!institutionLabel;

  const roleHtml = getRichHtml(values.role);
  const departmentHtml = getRichHtml(values.department);
  const researchHtml = getRichHtml(values.research_priorities);
  const functionsHtml = getRichHtml(values.functions);

  const hasRole = visibleSet.has('role') && !!roleHtml;
  const hasDepartment = visibleSet.has('department') && !!departmentHtml;
  const hasResearch = visibleSet.has('research_priorities') && !!researchHtml;
  const hasFunctions = visibleSet.has('functions') && !!functionsHtml;
  const hasFaculty =
    visibleSet.has('faculty') &&
    Array.isArray(values.faculty) &&
    values.faculty.length > 0;

  const hasAddressFields =
    (visibleSet.has('street') && values.street) ||
    (visibleSet.has('room') && values.room) ||
    (visibleSet.has('postalcode') && values.postalcode) ||
    (visibleSet.has('location') && values.location);

  const showPhone = visibleSet.has('phone') && !!values.phone;
  const showEmail = visibleSet.has('email') && !!values.email;

  const hasDetailsToShow =
    hasDepartment ||
    hasResearch ||
    hasFunctions ||
    hasFaculty ||
    hasAddressFields ||
    showPhone ||
    showEmail;

  const accordionOrder = [];
  if (hasResearch) accordionOrder.push('research_priorities');
  if (hasFunctions) accordionOrder.push('functions');
  if (hasFaculty) accordionOrder.push('faculty');

  const lastAccordion = accordionOrder[accordionOrder.length - 1];

  const toggleAccordion = (key) => {
    setAccordions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderAccordionHtml = (key, label, html) => {
    const open = accordions[key];
    const isLast = key === lastAccordion;

    return (
      <div
        className={cx('contact-block__accordion', {
          'contact-block__accordion--last': isLast,
        })}
      >
        <button
          className="contact-block__accordion-toggle"
          onClick={() => toggleAccordion(key)}
          aria-expanded={open}
          type="button"
        >
          <span className="contact-block__accordion-label">{label}</span>
          <span
            className={cx('contact-block__accordion-icon', {
              'is-open': open,
            })}
          />
        </button>

        {open && (
          <div className="contact-block__accordion-body">
            <div
              className="contact-block__richtext"
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </div>
        )}
      </div>
    );
  };

  const renderAccordionChildren = (key, label, hasContent, children) => {
    const open = accordions[key];
    const isLast = key === lastAccordion;

    if (!hasContent) return null;

    return (
      <div
        className={cx('contact-block__accordion', {
          'contact-block__accordion--last': isLast,
        })}
      >
        <button
          className="contact-block__accordion-toggle"
          onClick={() => toggleAccordion(key)}
          aria-expanded={open}
          type="button"
        >
          <span className="contact-block__accordion-label">{label}</span>
          <span
            className={cx('contact-block__accordion-icon', {
              'is-open': open,
            })}
          />
        </button>

        {open && (
          <div className="contact-block__accordion-body">{children}</div>
        )}
      </div>
    );
  };

  return (
    <div className="contact-block-wrapper">
      {data.headline && (
        <h3 className="contact-block__headline contact-block__headline--outside">
          {data.headline}
        </h3>
      )}

      <div
        className={cx('contact-block', {
          [`contact-block--${theme}`]: true,
          'contact-block--open': isOpen,
          'contact-block--closed': !isOpen,
        })}
      >
        <div className="contact-block__inner">
          <div
            className={cx('contact-block__top', {
              'contact-block__top--has-role': hasRole,
            })}
          >
            {visibleSet.has('image') && values.image && (
              <div className="contact-block__avatar">
                <div className="contact-block__avatar-inner">
                  <Image src={values.image} alt={titleForAlt} />
                </div>
              </div>
            )}

            <div className="contact-block__top-text">
              {(line1 || line2) && (
                <h3 className="contact-block__name">
                  {line1 && <span>{line1}</span>}
                  {line2 && <span>{line2}</span>}
                </h3>
              )}

              {hasInstitution && (
                <div className="contact-block__institution">
                  {hasInstitutionLabel && (
                    <div className="contact-block__institution-label">
                      {institutionLabel}
                    </div>
                  )}
                  <div className="contact-block__institution-name">
                    {values.institution}
                  </div>
                </div>
              )}
            </div>
          </div>

          {hasRole && (
            <div
              className="contact-block__role contact-block__richtext"
              dangerouslySetInnerHTML={{ __html: roleHtml }}
            />
          )}

          {isOpen && hasDetailsToShow && (
            <div className="contact-block__details">
              <div className="contact-block__details-white">
                {hasDepartment && (
                  <section className="contact-block__section contact-block__section--department">
                    <div className="contact-block__section-title">
                      {intl.formatMessage(messages.contactChoiceDepartment)}
                    </div>
                    <div
                      className="contact-block__section-body contact-block__richtext"
                      dangerouslySetInnerHTML={{ __html: departmentHtml }}
                    />
                  </section>
                )}

                {hasResearch &&
                  renderAccordionHtml(
                    'research_priorities',
                    intl.formatMessage(messages.contactChoicePriority),
                    researchHtml,
                  )}

                {hasFunctions &&
                  renderAccordionHtml(
                    'functions',
                    intl.formatMessage(messages.contactChoiceFunctions),
                    functionsHtml,
                  )}

                {hasFaculty &&
                  renderAccordionChildren(
                    'faculty',
                    intl.formatMessage(messages.contactBlockFaculty),
                    hasFaculty,
                    <ul className="contact-block__list">
                      {values.faculty.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>,
                  )}
              </div>

              {(hasAddressFields || showPhone || showEmail) && (
                <div className="contact-block__footer">
                  {hasAddressFields && (
                    <div className="contact-block__address">
                      {visibleSet.has('street') && values.street && (
                        <p>{values.street}</p>
                      )}
                      {visibleSet.has('room') && values.room && (
                        <p>{values.room}</p>
                      )}
                      {(visibleSet.has('postalcode') ||
                        visibleSet.has('location')) && (
                        <p>
                          {[
                            visibleSet.has('postalcode')
                              ? values.postalcode
                              : null,
                            visibleSet.has('location') ? values.location : null,
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        </p>
                      )}
                    </div>
                  )}

                  {(showPhone || showEmail) && (
                    <div className="contact-block__contact">
                      {showPhone && (
                        <p className="contact-block__contact-row">
                          <span className="contact-block__contact-label">
                            {intl.formatMessage(messages.contactChoicePhone)}:
                          </span>
                          <a href={`tel:${values.phone}`}>{values.phone}</a>
                        </p>
                      )}

                      {showEmail && (
                        <p className="contact-block__contact-row">
                          <span className="contact-block__contact-label">
                            {intl.formatMessage(messages.contactChoiceEmail)}:
                          </span>
                          <a href={`mailto:${values.email}`}>{values.email}</a>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {hasDetailsToShow && (
            <button
              className="contact-block__toggle"
              type="button"
              onClick={() => setIsOpen((o) => !o)}
            >
              <span className="contact-block__toggle-label">
                {isOpen
                  ? intl.formatMessage(messages.contactToggleClose)
                  : intl.formatMessage(messages.contactToggleOpen)}
              </span>

              <span
                className={cx('contact-block__toggle-icon', {
                  'is-open': isOpen,
                })}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactView;
